import cloneDeep from 'lodash/cloneDeep'
import * as d3 from 'd3'

class TemperatureLineChart {
  constructor(el, path, withXAxis = true) {
    this.el = el
    this._buildSvg()
    this._buildChartContainer()
    this._setupResize()
    this.clipPath = path
    this.withXAxis = withXAxis

    this.legendYWidth = 25
  }

  update(data) {
    this.data = data ? this._prepData(data) : this.data
    this._combineValuations()
    this._draw()
    if (data.disableValue) {
      this.tip.attr('class', 'hidden')
    }
  }

  destroy() {
    // ensure all event listeners are cleaned up
    if (typeof window !== 'undefined') window.removeEventListener('resize', this._redrawChart)
  }

  _draw() {
    this.svg?.selectAll('g').remove()
    this._buildChartContainer()
    this._calcDimensions()
    this._shiftChartContainer()
    this._calcScales()
    this._axes()
    this._drawGradient()
    this._segments() // Copied from ValueLineChart, requires data transform as is
  }

  _buildSvg() {
    this.svg = d3.select(this.el)
  }

  _buildChartContainer() {
    this.chart = this.svg?.append('g')
  }

  _shiftChartContainer() {
    this.chart?.attr('transform', `translate(${this.margin.left},${this.margin.top})`)
  }

  _axes() {
    this.withXAxis && this._drawXAxis()
    this._drawYAxis()
  }

  _drawLines() {
    const line = d3
      .line()
      .curve(d3.curveCardinal)
      .x(d => this.scaleX(d.date))
      .y(d => this.scaleY(d.value))

    this.chart
      ?.append('path')
      .datum(this.allValuations)
      .attr('class', 'line')
      .style('stroke', 'url(#temperature-gradient)')
      .attr('d', line)
  }

  _drawGradient() {
    this.chart
      ?.append('linearGradient')
      .attr('id', 'temperature-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', '100%')
      .selectAll('stop')
      .data([
        { offset: '0%', color: 'var(--brand-market-temp-hot)' },
        { offset: '27%', color: 'var(--brand-market-temp-warm)' },
        { offset: '53%', color: 'var(--brand-market-temp-neutral)' },
        { offset: '74%', color: 'var(--brand-market-temp-cool)' },
        { offset: '100%', color: 'var(--brand-market-temp-cold)' }
      ])
      .enter()
      .append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color)
  }

  _segments() {
    const line = d3
      .line?.()
      .curve(d3.curveNatural)
      .x(d => this.scaleX(d.date))
      .y((d, i) => this.scaleY(d.valuation))

    this.segment = this.chart
      ?.selectAll('.segment')
      .data(this.data.segments)
      .enter()
      .append('g')
      .attr('class', 'segment')

    this.segment
      ?.filter(d => d.valuations.length > 1)
      .append('path')
      .attr('class', 'line')
      .attr('d', d => line(d.valuations))
      .style('stroke', 'url(#temperature-gradient)')

    this.segment
      ?.selectAll('g')
      .data(d => d.valuations)
      .enter()
      .append('g')
      .classed('datapoint', true)
      .call(parentDatapoint => {
        this._addDataPoints(parentDatapoint)
      })
  }

  _addDataPoints(datapoint) {
    this._pointBorders(datapoint)
    this._points(datapoint)
  }

  _points(datapoint) {
    datapoint
      ?.append('circle')
      .classed('point', true)
      .attr('cx', d => this.scaleX(d.date))
      .attr('cy', d => this.scaleY(d.valuation))
      .attr('r', 3.5)
      .style('fill', 'url(#temperature-gradient)')
  }

  _pointBorders(datapoint) {
    datapoint
      ?.filter((d, i, nodes) => i === this.allValuations.length - 1)
      .append('circle')
      .classed('point-border', true)
      .style('stroke', 'url(#temperature-gradient)')
      .attr('cx', d => this.scaleX(d.date))
      .attr('cy', d => this.scaleY(d.valuation))
      .attr('r', 7.5)
  }

  _drawXAxis() {
    const xAxis = d3
      .axisBottom(this.scaleX)
      .tickFormat(d3.timeFormat("%b '%y"))
      .tickValues(
        this.allValuations.filter((_, i) => (i + 1) % 4 === this.allValuations.length % 4).map(val => val?.date)
      )

    this.chart
      ?.append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', `translate(0,${this.chartHeight})`)
      .call(xAxis)
  }

  _drawYAxis() {
    if (this.data.disableValue) return
    const yAxis = d3.axisRight(this.scaleY).tickSize(this.outerWidth).tickValues([0, 25, 50, 75, 100])

    // Add to outer svg because it's outside the graph on the left
    // Prepend instead of append so that it's under the chart
    this.yAxis = this.svg
      ?.insert('g', ':first-child')
      .attr('class', 'axis axis--y')
      .attr('transform', `translate(0, ${this.margin.top})`)
      .call(yAxis)

    this.yAxis
      ?.selectAll('line')
      .attr('transform', d => `translate(0, ${this.scaleY(d) * -1})`)
      .attr('y1', d => this.scaleY(d))
      .attr('y2', d => this.scaleY(d))
      .style('stroke-width', '2')
      .style('stroke-dasharray', '7 5')
      .style('stroke', 'url(#temperature-gradient)')
      .style('opacity', d => Math.abs(50 - d) / 150 + 0.5)

    this.yAxis?.selectAll('text').attr('x', 0).style('display', 'none')

    this.legendY = this.yAxis?.insert('g', ':first-child').attr('transform', `translate(12, 0)`)

    this.legendY
      ?.insert('line', ':first-child')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', -1)
      .attr('y2', this.chartHeight + 2)
      .style('stroke-width', this.legendYWidth)
      .style('stroke', 'url(#temperature-gradient)')

    const cosmetics = {
      fill: 'var(--brand-hb-black)',
      fontWeight: '700',
      fontSize: '12px',
      textTransform: 'uppercase'
    }

    this.legendY
      ?.append('g')
      .attr('transform', `translate(4, ${this.chartHeight - 5})`)
      .append('text')
      .attr('fill', cosmetics.fill)
      .attr('textLength', `${this.chartHeight * 0.4}`)
      .attr('transform', 'rotate(-90)')
      .style('font-size', cosmetics.fontSize)
      .style('font-weight', cosmetics.fontWeight)
      .style('text-transform', cosmetics.textTransform)
      .html('Easier to Buy')

    this.legendY
      ?.append('g')
      .attr('transform', `translate(4, ${this.chartHeight * 0.4 + 10})`)
      .append('text')
      .attr('fill', cosmetics.fill)
      .attr('textLength', `${this.chartHeight * 0.4}`)
      .attr('transform', 'rotate(-90)')
      .style('font-size', cosmetics.fontSize)
      .style('font-weight', cosmetics.fontWeight)
      .style('text-transform', cosmetics.textTransform)
      .html('Easier to Sell')
  }

  _calcDimensions() {
    this.margin = {
      top: 0,
      right: 25,
      bottom: 30,
      left: this.legendYWidth + 15
    }
    this.outerWidth = this.el?.getBoundingClientRect().width
    this.outerHeight = this.el?.getBoundingClientRect().height
    this.chartWidth = this.outerWidth - this.margin.left - this.margin.right
    this.chartHeight = this.outerHeight - this.margin.top - this.margin.bottom
  }

  _calcScales() {
    this.scaleX = d3.scaleTime().domain(this.xDomain).range([0, this.chartWidth])
    this.scaleY = d3.scaleLinear().domain([0, 100]).range([this.chartHeight, 0])
  }

  _getValues() {
    const data = cloneDeep(this.data)
    this.allValues = data.datapoints
  }

  _setupResize() {
    if (this._redrawChart) return

    this._redrawChart = () => {
      this._draw()
    }

    if (typeof window !== 'undefined') window.addEventListener('resize', this._redrawChart)
  }

  _combineValuations() {
    const data = cloneDeep(this.data)
    let valuations = []
    data.segments.forEach(segment => (valuations = valuations.concat(segment.valuations)))
    this.allValuations = valuations
  }

  _prepData(data) {
    const transformedData = cloneDeep(data)

    return transformedData
  }

  get xDomain() {
    return d3.extent(this.allValuations, d => d.date)
  }
}

export default TemperatureLineChart
